// Reusable paint-system coats modal. Pass paintSystem, or projectId + finish (cached per project).
import React, { Fragment, useEffect, useState } from "react";
import { Modal, ModalHeader, ModalBody } from "reactstrap";
import axios from "axios";

const cacheByKey = {};
const cacheByProject = {};
const inflight = {};

export function finishCode(finish) {
  return String(finish || "").replace(/X$/i, "");
}

export function paintSystemCacheKey(projectId, finish) {
  if (!projectId || !finish) return null;
  return `${projectId}:${finishCode(finish)}`;
}

export function primePaintSystemCache(paint_systems) {
  _.each(paint_systems, (system, key) => {
    if (system) cacheByKey[key] = system;
  });
}

function findSystem(systems, finish) {
  const code = finishCode(finish);
  return _.find(systems, (ps) => ps.code == code || ps.display_code == code || ps.code == finish || ps.display_code == finish);
}

function loadProjectSystems(projectId) {
  if (cacheByProject[projectId]) return Promise.resolve(cacheByProject[projectId]);
  if (inflight[projectId]) return inflight[projectId];
  inflight[projectId] = axios
    .get(`/project/${projectId}/technical_details/paint_systems.json`)
    .then((res) => {
      cacheByProject[projectId] = res.data;
      delete inflight[projectId];
      return res.data;
    })
    .catch((err) => {
      delete inflight[projectId];
      throw err;
    });
  return inflight[projectId];
}

function sectionMatches(sectionSize, section) {
  if (!sectionSize || !section?.strumis_section_size) return false;
  return String(section.strumis_section_size) == String(sectionSize).split("-")[0];
}

const CoatsTable = ({ paintSystem, sectionSize }) => {
  if (paintSystem?.is_multi_system) {
    return (
      <Fragment>
        <div className="mb-2">
          {paintSystem.display_code} is a multi paint system
          {paintSystem.systems_sentence ? `: ${paintSystem.systems_sentence}` : ""}
        </div>
        {_.map(paintSystem.systems || [], (child, i) => (
          <div key={i} className="mb-3">
            <b>{child.display_code}</b>
            <CoatsTable paintSystem={child} sectionSize={sectionSize} />
          </div>
        ))}
      </Fragment>
    );
  }

  const coats = _.filter(paintSystem?.coats || paintSystem?.coats_attributes || [], (coat) => !coat.blast_profile);
  return (
    <Fragment>
      {(paintSystem?.surface_prep_name || paintSystem?.code_details_name) && (
        <div className="mb-2">
          {paintSystem.code_details_name && <span>{paintSystem.code_details_name} system. </span>}
          {paintSystem.surface_prep_name && <span>Surface Preparation: {paintSystem.surface_prep_name}</span>}
        </div>
      )}
      <table className="w-100" size="sm">
        <thead>
          <tr>
            <th>Coat No</th>
            <th>Product</th>
            <th>Colour</th>
            <th>DFT</th>
            <th>Location</th>
          </tr>
        </thead>
        <tbody>
          {_.map(coats, (coat, i) => (
            <Fragment key={coat.id || i}>
              <tr>
                <td>{coat.number}</td>
                <td>{coat.display_name || coat.product_name}</td>
                <td>{coat.color}</td>
                <td>{coat.dft_by_sections ? <b>By Section Sizes</b> : <span>{coat.dft}&micro;</span>}</td>
                <td>{coat.location_name}</td>
              </tr>
              {coat.dft_by_sections &&
                _.map(coat.sections || coat.sections_attributes, (section, si) => {
                  const match = sectionMatches(sectionSize, section);
                  return (
                    <tr key={section.id || si} style={match ? { backgroundColor: "#fff3cd" } : undefined}>
                      <td></td>
                      <td className="pl-4">
                        - {section.strumis_section_type} &gt; {section.strumis_section_size}
                        {match && " (this mark)"}
                      </td>
                      <td></td>
                      <td>{section.dft}&micro;</td>
                      <td></td>
                    </tr>
                  );
                })}
            </Fragment>
          ))}
        </tbody>
      </table>
      {paintSystem?.treatment_grade_name && (
        <div className="mt-2">
          <b>Treatment Grade: {paintSystem.treatment_grade_name}</b>
        </div>
      )}
    </Fragment>
  );
};

const PaintSystemCoatsModal = (props) => {
  const { isOpen, toggle, paintSystem, projectId, finish, sectionSize } = props;
  const [loaded, setLoaded] = useState(paintSystem || null);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    if (paintSystem) {
      setLoaded(paintSystem);
      setStatus(null);
      return;
    }
    const key = paintSystemCacheKey(projectId, finish);
    if (key && cacheByKey[key]) {
      setLoaded(cacheByKey[key]);
      setStatus(null);
      return;
    }
    if (!projectId || !finish) {
      setLoaded(null);
      setStatus("No Lion project linked for this finish");
      return;
    }
    setLoaded(null);
    setStatus("Loading...");
    loadProjectSystems(projectId)
      .then((systems) => {
        const found = findSystem(systems, finish);
        if (found && key) cacheByKey[key] = found;
        setLoaded(found || null);
        setStatus(found ? null : `No paint system found for ${finish}`);
      })
      .catch(() => {
        setLoaded(null);
        setStatus(`No paint system found for ${finish}`);
      });
  }, [isOpen, paintSystem, projectId, finish]);

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" zIndex={1050}>
      <ModalHeader toggle={toggle}>
        {loaded?.display_code || finish || "Paint System"}
        {sectionSize ? ` — section ${sectionSize}` : ""}
      </ModalHeader>
      <ModalBody>
        {status && <div>{status}</div>}
        {loaded && <CoatsTable paintSystem={loaded} sectionSize={sectionSize} />}
      </ModalBody>
    </Modal>
  );
};

export default PaintSystemCoatsModal;
